import { useCallback, useEffect, useMemo, useState } from "react";

import { Pen } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  useAssignCurator,
  useMyCurators,
  useUnassignCurator,
} from "@/entities/curator";

import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import { PAGE_SIZE } from "@/shared/lib/consts";
import { Checkbox } from "@/shared/ui/checkbox";
import { Modal } from "@/shared/ui/Modal";
import { CreateCuratorForm } from "./ui/CreateCuratorForm";
import { useIsolatedPages } from "@/shared/hooks/useIsolatedPages";
import { TableSearchInput } from "@/features/TableSearchInput/TableSearchInput";
import { useToast } from "@/shared/hooks/useToast";
import { Spinner } from "@/shared/ui/spinner";

export const AccountReassignModalPreRelease = ({
  accounts: initialAccounts,
  managerId,
  open,
  onOpenChange,
}: {
  accounts: string[];
  managerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { page, setPage, search, setSearch } = useIsolatedPages();
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(
    new Set(initialAccounts),
  );
  const [pendingChanges, setPendingChanges] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);

  const { data, isPending } = useMyCurators({
    pageSize: PAGE_SIZE,
    currentPage: page,
    search,
    enabled: open,
  });

  const { mutateAsync: assignCurator } = useAssignCurator();
  const { mutateAsync: unassignCurator } = useUnassignCurator();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    setSelectedAccounts(new Set(initialAccounts));
  }, [initialAccounts]);

  const handleNextPage = useCallback(
    () => setPage((prev) => prev + 1),
    [setPage],
  );
  const handlePrevPage = useCallback(
    () => setPage((prev) => Math.max(prev - 1, 1)),
    [setPage],
  );

  const handleToggleCurator = useCallback(
    (curatorId: string) => {
      setSelectedAccounts((prev) => {
        const newSet = new Set(prev);
        const isSelected = newSet.has(curatorId);

        if (isSelected) {
          newSet.delete(curatorId);
        } else {
          newSet.add(curatorId);
        }

        setPendingChanges((prevPending) => {
          const pendingSet = new Set(prevPending);
          const wasInitiallySelected = initialAccounts.includes(curatorId);

          if (wasInitiallySelected === !isSelected) {
            pendingSet.delete(curatorId);
          } else {
            pendingSet.add(curatorId);
          }

          return pendingSet;
        });

        return newSet;
      });
    },
    [initialAccounts],
  );

  const handleApplyChanges = async () => {
    const toAssign = Array.from(pendingChanges).filter((id) =>
      selectedAccounts.has(id),
    );
    const toUnassign = Array.from(pendingChanges).filter(
      (id) => !selectedAccounts.has(id),
    );

    setLoading(true);

    try {
      await Promise.all([
        ...toAssign.map((curatorId) => assignCurator({ managerId, curatorId })),
        ...toUnassign.map((curatorId) =>
          unassignCurator({ managerId, curatorId }),
        ),
      ]);

      await Promise.all(
        [
          "managersByMasterteamlead",
          "managersByTeamlead",
          "managers",
          "curatorsMy",
        ].map((queryKey) =>
          queryClient.invalidateQueries({
            queryKey: [queryKey],
          }),
        ),
      );
      toast({
        title: "Успешно",
        description: "Изменения успешно применены",
        variant: "default",
      });

      setPendingChanges(new Set());
      onOpenChange(false);
    } catch (error) {
      console.error("Ошибка при применении изменений:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось применить изменения",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = useMemo(
    () => Math.ceil((data?.total || 0) / PAGE_SIZE),
    [data],
  );

  return (
    <Modal onOpenChange={onOpenChange} open={open}>
      <Modal.Trigger>
        <Pen />
      </Modal.Trigger>
      <Modal.Content className="lg:min-w-[700px]">
        <Modal.Title>Привязка аккаунтов</Modal.Title>
        <CreateCuratorForm />
        <TableSearchInput
          search={search}
          setSearch={setSearch}
          setPage={setPage}
        />
        <ul className="pt-2">
          {isPending
            ? Array.from({ length: 4 }).map((_, idx) => (
                <li key={idx}>
                  <Skeleton />
                </li>
              ))
            : data?.data.map((curator) => (
                <li
                  key={curator.tgId}
                  className="flex items-center justify-between"
                >
                  <span className="text-textColor">{curator.tgUsername}</span>
                  <Checkbox
                    checked={selectedAccounts.has(curator._id)}
                    onClick={() => handleToggleCurator(curator._id)}
                  />
                </li>
              ))}
        </ul>
        <Button
          onClick={handleApplyChanges}
          disabled={pendingChanges.size === 0 || loading}
        >
          {!loading ? (
            `Применить изменения ${pendingChanges.size !== 0 ? pendingChanges.size : ""}`
          ) : (
            <Spinner className="animate-spin" />
          )}
        </Button>
        <div className="mt-4 flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevPage}
            disabled={page === 1 || !data}
          >
            <p className="text-textColor">Предыдущая</p>
          </Button>
          {totalPages > 0 && (
            <span className="text-textColor">
              {page} из {totalPages}
            </span>
          )}
          <Button
            variant="outline"
            onClick={handleNextPage}
            disabled={page >= totalPages || !data}
          >
            <p className="text-textColor">Следующая</p>
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};
